/**
 * ArtefactLink
 * Renders clickable links for artefact references.
 * Parses hrefs in format: #/project/:projectId/:paneType/:artefactId
 * and navigates to the corresponding project pane.
 *
 * Used as a custom `a` component inside react-markdown.
 */

import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';

interface ArtefactLinkProps {
  href?: string;
  children?: React.ReactNode;
}

/** Artefact href pattern: #/project/:projectId/:paneType/:artefactId */
const ARTEFACT_HREF_RE = /^#\/project\/([^/]+)\/([^/]+)\/([^/]+)$/;

function parseArtefactHref(href: string) {
  const match = href.match(ARTEFACT_HREF_RE);
  if (!match) return null;
  return { projectId: match[1], paneType: match[2], artefactId: match[3] };
}

/**
 * Renders a link. If the href matches the artefact reference pattern,
 * it navigates to the appropriate project pane. Otherwise renders a
 * standard anchor that opens in a new tab.
 */
export function ArtefactLink({ href = '', children }: ArtefactLinkProps) {
  const navigate = useNavigate();
  const { wsSlug } = useWorkspace();
  const parsed = parseArtefactHref(href);

  if (!parsed) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
        {children}
      </a>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    try {
      const path = `/pr/${parsed.projectId}/${parsed.paneType}/${parsed.artefactId}`;
      navigate(wsSlug ? `/w/${wsSlug}${path}` : path);
    } catch (err) {
      console.error('[ArtefactLink] navigation failed:', href, err);
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline cursor-pointer"
    >
      {children}
    </a>
  );
}
